import { CloudUploadOutlined, DeleteOutlined } from '@ant-design/icons';
import { Button, Divider, Flex, Input, Spin, Typography } from 'antd';

import {
  filesExtensions,
  MB_COUNT,
  MB_RATIO,
} from '@app/components/DraggerComponent/data/constants';
import type { NDraggerComponent } from '@app/components/DraggerComponent/DraggerComponent.types';
import { useDraggerComponent } from '@app/components/DraggerComponent/hooks/useDraggerComponent';
import { getFileImage } from '@app/utils';

import '@app/components/DraggerComponent/styles/DraggerComponent.desktop.scss';

const { Text } = Typography;

export const DraggerComponent = ({
  id,
  filesWithData,
  setFilesWithData,
  isChat,
  isFilesSizeExceeded,
  totalSize,
  onLoadStart,
  onLoadEnd,
  isLoading = false,
  extraExtensions = [],
}: NDraggerComponent.Main) => {
  const {
    drag,
    dragStartHandler,
    dragLeaveHandler,
    onDropHandler,
    deleteFileFromTheList,
    handleDescriptionChange,
    uploadFileManually,
    handleFileNameChange,
    fileInputRef,
    handleDragEnter,
    isFileLoading,
  } = useDraggerComponent({
    id,
    setFilesWithData,
    isChat,
    onLoadStart,
    onLoadEnd,
    extraExtensions,
  });

  return (
    <div className="dragger-container">
      <div className="dragger-window">
        <div
          className={`${drag ? 'drop' : 'dragger'}-area`}
          onDragEnter={handleDragEnter}
          onDragStart={dragStartHandler}
          onDragLeave={dragLeaveHandler}
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDropHandler}
        >
          {isFileLoading || isLoading ? (
            <Flex vertical align="center" justify="center" gap={12}>
              <Spin spinning />
              <Text>
                Идет загрузка... Не закрывайте окно пока загружаются файлы.
              </Text>
            </Flex>
          ) : (
            <>
              <Text strong className="bc-dragger__hint">
                <CloudUploadOutlined /> Перетащите файлы сюда или
                <Button
                  onClick={() => {
                    fileInputRef.current?.click();
                  }}
                  type="link"
                  style={{ padding: '4px 4px' }}
                  data-testid="add-files"
                  disabled={isFileLoading}
                >
                  нажмите для выбора
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    multiple
                    accept={[...filesExtensions, ...extraExtensions].join(', ')}
                    onChange={uploadFileManually}
                    disabled={isFileLoading}
                  />
                </Button>
              </Text>
              <div className="footer">
                <div style={{ fontSize: '10px', color: 'rgba(0, 0, 0, 0.45)' }}>
                  Макс. размер файла: {MB_COUNT} Мб
                </div>
                <div style={{ fontSize: '10px', color: 'rgba(0, 0, 0, 0.45)' }}>
                  Допустимые форматы:{' '}
                  {[...filesExtensions, ...extraExtensions].join(', ')}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="items-list">
        {filesWithData.map(({ FileName }, index) => {
          const isLast = index === filesWithData.length - 1;
          return (
            <div key={FileName}>
              <div className="row-container">
                <div>{getFileImage({ fileName: FileName, filesWithData })}</div>

                <div className="info-container">
                  <div>
                    <Input
                      addonAfter={
                        FileName ? `.${FileName.split('.').pop()}` : ''
                      }
                      defaultValue={FileName ? FileName.split('.').shift() : ''}
                      onChange={(e) => handleFileNameChange(e, FileName)}
                      disabled={isFileLoading || isLoading}
                    />
                  </div>

                  <div>
                    <Input
                      placeholder="Введите описание файла"
                      minLength={1}
                      maxLength={400}
                      onChange={(e) => handleDescriptionChange(e, FileName)}
                      disabled={isFileLoading || isLoading}
                    />
                  </div>
                </div>

                <div>
                  <Button
                    icon={<DeleteOutlined />}
                    onClick={(e) => deleteFileFromTheList(FileName, e)}
                    disabled={isFileLoading || isLoading}
                  />
                </div>
              </div>
              {!isLast && <Divider />}
            </div>
          );
        })}
      </div>

      {isFilesSizeExceeded && (
        <Text type="danger" className="full-width">
          <div className="error-message">
            Суммарный размер файлов ({(totalSize / MB_RATIO).toFixed(1)} Мб)
            превышает максимальный ({MB_COUNT} Мб).
          </div>
        </Text>
      )}
    </div>
  );
};
